import React, { useMemo } from 'react';
import { Box, Typography, alpha, useTheme } from '@mui/material';
import { checkIcon, FaOptionIcon } from '@europrocurement/l2d-icons';

interface ThemeOptionCardProps {
    /**
     * Couleur de fond de la carte (généralement blanc pour light, noir/gris foncé pour dark)
     */
    backgroundColor: string;

    /**
     * Couleur primaire (visible sur le label, la première barre et la coche)
     */
    primaryColor: string;

    /**
     * Couleur secondaire (visible sur la deuxième barre avec transparence)
     */
    secondaryColor: string;

    /**
     * Texte affiché (ex: "Clair", "Sombre", "Auto", "Bleu", etc.)
     */
    label: string;

    /**
     * Si cette option est actuellement sélectionnée
     */
    isSelected?: boolean;

    /**
     * Si c'est l'option "Auto" qui a un design spécial (moitié clair/sombre)
     */
    isAutoMode?: boolean;

    /**
     * Désactive l'option
     */
    isDisabled?: boolean;

    /**
     * Effets appliqués à l'élément
     */
    hasSpecialEffect?: boolean;

    /**
     * Callback déclenché au clic
     */
    onClick?: () => void;
}

const ThemeOptionCard: React.FunctionComponent<ThemeOptionCardProps> = function ({
    backgroundColor,
    primaryColor,
    secondaryColor,
    label,
    isSelected = false,
    isAutoMode = false,
    isDisabled = false,
    hasSpecialEffect = false,
    onClick,
}) {
    const { palette } = useTheme();
    const handleClick = () => {
        if (!isDisabled && onClick) {
            onClick();
        }
    };

    const isLightTheme = useMemo(
        () =>
            backgroundColor === '#ffffff' ||
            backgroundColor === 'white' ||
            backgroundColor === palette.background.paper,
        [backgroundColor, palette.background.paper],
    );

    const colors = useMemo(() => {
        let sidebarBg: string;
        let contentBg: string;
        let lightGrayBar1: string;
        let lightGrayBar2: string;
        let darkGrayBar1: string;
        let darkGrayBar2: string;
        let inactiveBorderColor: string;
        let containerBg: string;

        if (isLightTheme) {
            sidebarBg = '#ffffff';
            contentBg = '#f5f5f5';
            lightGrayBar1 = '#e0e0e0';
            lightGrayBar2 = '#eeeeee';
            darkGrayBar1 = '#bdbdbd';
            darkGrayBar2 = '#d0d0d0';
            inactiveBorderColor = '#e0e0e0';
            containerBg = '#ffffff';
        } else {
            sidebarBg = '#1e1e1e';
            contentBg = '#0f0f0f';
            lightGrayBar1 = '#444444';
            lightGrayBar2 = '#333333';
            darkGrayBar1 = '#666666';
            darkGrayBar2 = '#555555';
            inactiveBorderColor = '#505050';
            containerBg = '#1a1a1a';
        }

        return {
            sidebarBg,
            contentBg,
            lightGrayBar1,
            lightGrayBar2,
            darkGrayBar1,
            darkGrayBar2,
            inactiveBorderColor,
            containerBg,
        };
    }, [isLightTheme]);

    const borderRadius = 1;

    const renderAutoMode = () => (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                borderRadius,
                overflow: 'hidden',
            }}
        >
            {/* Sidebar claire */}
            <Box
                sx={{
                    width: '40px',
                    backgroundColor: '#ffffff',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    padding: '8px 4px',
                    flexShrink: 0,
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        height: '8px',
                        backgroundColor: primaryColor,
                        borderRadius: '24px',
                    }}
                />
                <Box
                    sx={{
                        width: '100%',
                        height: '8px',
                        backgroundColor: alpha(secondaryColor, 0.6),
                        borderRadius: '24px',
                    }}
                />
                <Box
                    sx={{
                        width: '100%',
                        height: '8px',
                        backgroundColor: '#e0e0e0',
                        borderRadius: '24px',
                    }}
                />
                <Box
                    sx={{
                        width: '100%',
                        height: '8px',
                        backgroundColor: '#eeeeee',
                        borderRadius: '24px',
                    }}
                />
            </Box>

            {/* Zone de contenu avec label en superposition */}
            <Box
                sx={{
                    flex: 1,
                    height: '100%',
                    display: 'flex',
                    position: 'relative', // Pour le positionnement du label
                }}
            >
                {/* Moitié gauche (claire) */}
                <Box sx={{ width: '33.33%', height: '100%', display: 'flex' }}>
                    {/* Contenu clair */}
                    <Box
                        sx={{
                            flex: 1,
                            backgroundColor: '#f5f5f5',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                            justifyContent: 'center',
                            padding: '2px 4px',
                        }}
                    >
                        <Box
                            sx={{
                                width: '100%',
                                height: '8px',
                                backgroundColor: '#bdbdbd',
                                borderRadius: '24px 0px 0px 24px',
                            }}
                        />
                        <Box
                            sx={{
                                width: '100%',
                                height: '8px',
                                backgroundColor: '#d0d0d0',
                                borderRadius: '24px 0px 0px 24px',
                            }}
                        />
                    </Box>
                </Box>

                {/* Moitié droite (sombre) */}
                <Box sx={{ width: '66.67%', height: '100%', display: 'flex' }}>
                    {/* Contenu sombre */}
                    <Box
                        sx={{
                            flex: 1,
                            backgroundColor: '#0f0f0f',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                            justifyContent: 'center',
                            padding: '2px 4px',
                        }}
                    >
                        <Box
                            sx={{
                                width: '65%',
                                height: '8px',
                                backgroundColor: '#666666',
                                borderRadius: '0px 24px 24px 0px',
                            }}
                        />
                        <Box
                            sx={{
                                width: '90%',
                                height: '8px',
                                backgroundColor: '#555555',
                                borderRadius: '0px 24px 24px 0px',
                            }}
                        />
                    </Box>
                </Box>

                {/* Label avec icône en superposition */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 2,
                        left: 4,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        zIndex: 1,
                        pointerEvents: 'none',
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{
                            color: primaryColor,
                            fontWeight: 600,
                            fontSize: '16px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {label}
                    </Typography>

                    {isSelected && (
                        <FaOptionIcon
                            icon={checkIcon.props.icon}
                            sx={{
                                color: primaryColor,
                                fontSize: 12,
                                flexShrink: 0,
                            }}
                        />
                    )}
                </Box>
            </Box>
        </Box>
    );

    const renderNormalMode = () => (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                borderRadius,
                overflow: 'hidden',
            }}
        >
            {/* Sidebar (navbar verticale) */}
            <Box
                sx={{
                    width: '40px',
                    backgroundColor: colors.sidebarBg,
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '8px 4px',
                    gap: 0.5,
                    flexShrink: 0,
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        height: '8px',
                        backgroundColor: primaryColor,
                        borderRadius: '24px',
                    }}
                />
                <Box
                    sx={{
                        width: '100%',
                        height: '8px',
                        backgroundColor: alpha(secondaryColor, 0.6),
                        borderRadius: '24px',
                    }}
                />
                <Box
                    sx={{
                        width: '100%',
                        height: '8px',
                        backgroundColor: colors.lightGrayBar1,
                        borderRadius: '24px',
                    }}
                />
                <Box
                    sx={{
                        width: '100%',
                        height: '8px',
                        backgroundColor: colors.lightGrayBar2,
                        borderRadius: '24px',
                    }}
                />
            </Box>

            {/* Contenu */}
            <Box
                sx={{
                    flex: 1,
                    backgroundColor: colors.contentBg,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'start',
                    padding: '2px 8px',
                    gap: '3px',
                    overflow: 'hidden',
                }}
            >
                {/* Label en haut */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        minHeight: 20,
                        width: '100%', // Assure que le container prend toute la largeur
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{
                            color: primaryColor,
                            fontWeight: 600,
                            fontSize: '16px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            minWidth: 0, // Permet au texte de se réduire en dessous de sa taille naturelle
                        }}
                    >
                        {label}
                    </Typography>

                    {isSelected && (
                        <FaOptionIcon
                            icon={checkIcon.props.icon}
                            sx={{
                                color: primaryColor,
                                fontSize: 12,
                                marginLeft: 'auto', // Pousse l'icône à droite
                                flexShrink: 0,
                            }}
                        />
                    )}
                </Box>

                {/* Barres en bas */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box
                        sx={{
                            width: '80%',
                            height: '8px',
                            backgroundColor: colors.darkGrayBar1,
                            borderRadius: '24px',
                        }}
                    />
                    <Box
                        sx={{
                            width: '100%',
                            height: '8px',
                            backgroundColor: colors.darkGrayBar2,
                            borderRadius: '24px',
                        }}
                    />
                </Box>
            </Box>
        </Box>
    );

    const containerBackgroundColor = useMemo(() => {
        if (isAutoMode) {
            return 'transparent';
        }
        return colors.containerBg;
    }, [isAutoMode, colors.containerBg]);

    const handleCursor = useMemo(() => {
        if (isDisabled) {
            return 'not-allowed';
        }

        if (onClick) {
            return 'pointer';
        }

        return 'default';
    }, [isDisabled, onClick]);

    return (
        <Box
            onClick={handleClick}
            sx={{
                width: 120,
                height: 80,
                border: 1.5,
                borderColor: isSelected ? primaryColor : colors.inactiveBorderColor,
                borderRadius,
                cursor: handleCursor,
                position: 'relative',
                transition: 'all 1s cubic-bezier(.47,1.64,.41,.8)',
                backgroundColor: containerBackgroundColor,
                opacity: isDisabled ? 0.5 : 1,
                userSelect: 'none',

                '&:hover':
                    onClick && !isDisabled
                        ? {
                              borderColor: isSelected ? primaryColor : alpha(primaryColor, 0.5),
                          }
                        : {},

                // Effet spécial UNIQUEMENT pour le mode auto activé
                ...(hasSpecialEffect &&
                    isAutoMode && {
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: -8,
                            left: -8,
                            right: -8,
                            bottom: -8,
                            borderRadius: '12px',
                            background: `linear-gradient(45deg, ${alpha(primaryColor, 0.3)}, ${alpha(primaryColor, 0.1)})`,
                            boxShadow: `0 0 20px ${alpha(primaryColor, 0.4)}`,
                            zIndex: -1,
                        },
                        transform: 'translateY(-1px)',
                        boxShadow: `0 8px 25px ${alpha(primaryColor, 0.5)}`,
                        transition: 'all 0.8s cubic-bezier(.47,1.64,.41,.8)',
                    }),

                // Pseudo-élément pour le fond gradient de l'auto mode
                overflow: 'hidden',
                ...(isAutoMode && {
                    '&::after': {
                        content: '""',
                        width: '110%',
                        height: '110%',
                        position: 'absolute',
                        top: -1,
                        left: -1,
                        right: -1,
                        bottom: -1,
                        background: 'linear-gradient(to right, #ffffff 40%, #1a1a1a 60%)',
                        borderRadius: '12px',
                        zIndex: -1,
                    },
                }),
            }}
        >
            {isAutoMode ? renderAutoMode() : renderNormalMode()}
        </Box>
    );
};

export default ThemeOptionCard;
